// src/components/ArcherChat/ArcherChat.jsx
//
// Floating AI chat widget — integrates into any page of the existing app.
// Uses MUI v5, Redux, existing fetchJSON auth, and your theme tokens.
// Drop <ArcherChat /> anywhere (e.g. App.js root) and it floats bottom-right.

import React, { useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Box, Fab, Drawer, Typography, TextField, IconButton,
  CircularProgress, Tooltip, Divider, Chip, Link,
  Table, TableBody, TableCell, TableHead, TableRow, Paper,
  useTheme, alpha,
} from '@mui/material';
import {
  AutoAwesome as AIIcon,
  Close as CloseIcon,
  Send as SendIcon,
  AddComment as NewChatIcon,
  OpenInNew as OpenInNewIcon,
  Code as SqlIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Storage as DbIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { BarChart, LineChart, PieChart } from '@mui/x-charts';
import {
  archerSendMessage,
  archerNewConversation,
  archerToggleWidget,
} from '../../redux/archerChat/actions';

// ── Hidden column filter — columns NEVER shown in DataGrid ───────────────────
// Add column names here to permanently hide them regardless of what SQL returns
const HIDE_COLS = /^(RecID|recid|RECID|.*RecID$|.*_recid$)/i;

// ── Navigation config — columns that become clickable links ──────────────────
// Add new entities here: { columns: [...possible col names], pattern: '/path/{id}' }
const NAV_ENTITIES = [
  { columns: ['LoanID','LoanRecID','LoanId','loan_id','Account','account'], pattern: '/loans/{id}' },
  { columns: ['LenderID','LenderRecID','LenderId','lender_id'],             pattern: '/lenders/{id}' },
  { columns: ['PropertyID','PropertyRecID','PropId','property_id','PropRecID'], pattern: '/properties/{id}' },
];
const BASE_URL = 'https://archer.korthdirect.com';

// Columns that show value text as link (not "View ↗")
const INLINE_LINK_COLS = /^(Account|account)$/i;

function resolveEntityUrl(colName, value) {
  const match = NAV_ENTITIES.find(e =>
    e.columns.some(c => c.toLowerCase() === colName.toLowerCase())
  );
  if (!match || !value) return null;
  return BASE_URL + match.pattern.replace('{id}', value);
}

// ── Markdown renderer — maps Markdown to MUI components ──────────────────────
function MarkdownContent({ children }) {
  const theme = useTheme();
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        h1: ({ children }) => (
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, mt: 1, fontSize: 14 }}>
            {children}
          </Typography>
        ),
        h2: ({ children }) => (
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5, mt: 1, fontSize: 13 }}>
            {children}
          </Typography>
        ),
        h3: ({ children }) => (
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, mt: 0.75, fontSize: 12 }}>
            {children}
          </Typography>
        ),
        p: ({ children }) => (
          <Typography sx={{ fontSize: 13, lineHeight: 1.7, mb: 0.75, color: 'text.primary' }}>
            {children}
          </Typography>
        ),
        strong: ({ children }) => (
          <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{children}</Box>
        ),
        em: ({ children }) => (
          <Box component="span" sx={{ fontStyle: 'italic' }}>{children}</Box>
        ),
        code: ({ inline, children }) =>
          inline ? (
            <Box component="code" sx={{
              px: 0.5, py: 0.1, borderRadius: 0.5, fontSize: 11,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: 'success.main', fontFamily: 'monospace',
            }}>
              {children}
            </Box>
          ) : (
            <Box component="pre" sx={{
              p: 1.5, borderRadius: 1, my: 1, fontSize: 11,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              color: 'success.main', fontFamily: 'monospace',
              overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
            }}>
              <code>{children}</code>
            </Box>
          ),
        ul: ({ children }) => (
          <Box component="ul" sx={{ pl: 2.5, mb: 0.75, mt: 0 }}>{children}</Box>
        ),
        ol: ({ children }) => (
          <Box component="ol" sx={{ pl: 2.5, mb: 0.75, mt: 0 }}>{children}</Box>
        ),
        li: ({ children }) => (
          <Box component="li" sx={{ fontSize: 13, lineHeight: 1.7, color: 'text.primary', mb: 0.25 }}>
            {children}
          </Box>
        ),
        hr: () => <Divider sx={{ my: 1 }} />,
        blockquote: ({ children }) => (
          <Box sx={{
            borderLeft: `3px solid ${theme.palette.primary.main}`,
            pl: 1.5, ml: 0, my: 0.75,
            color: 'text.secondary', fontStyle: 'italic',
          }}>
            {children}
          </Box>
        ),
        a: ({ href, children }) => (
          <Link href={href} target="_blank" rel="noreferrer"
            sx={{ color: 'primary.main', fontSize: 13 }}>
            {children}
          </Link>
        ),
        table: ({ children }) => (
          <Paper variant="outlined" sx={{ mt: 1, mb: 1, overflowX: 'auto' }}>
            <Table size="small">{children}</Table>
          </Paper>
        ),
        thead: ({ children }) => (
          <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
            {children}
          </TableHead>
        ),
        tbody: ({ children }) => <TableBody>{children}</TableBody>,
        tr: ({ children }) => (
          <TableRow sx={{ '&:last-child td': { border: 0 } }}>{children}</TableRow>
        ),
        th: ({ children }) => (
          <TableCell sx={{
            fontWeight: 700, fontSize: 11, color: 'text.secondary',
            borderBottom: `1px solid ${theme.palette.divider}`, py: 0.75, px: 1,
          }}>
            {children}
          </TableCell>
        ),
        td: ({ children }) => (
          <TableCell sx={{
            fontSize: 12, color: 'text.primary',
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
            py: 0.6, px: 1,
          }}>
            {children}
          </TableCell>
        ),
      }}
    >
      {children}
    </ReactMarkdown>
  );
}

// ── SQL disclosure ────────────────────────────────────────────────────────────
function SqlDisclosure({ sql }) {
  const [open, setOpen] = React.useState(false);
  const theme = useTheme();
  if (!sql) return null;
  return (
    <Box sx={{ mt: 1 }}>
      <Box
        onClick={() => setOpen(o => !o)}
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
              color: 'text.disabled', '&:hover': { color: 'text.secondary' } }}
      >
        <SqlIcon sx={{ fontSize: 13 }} />
        <Typography variant="caption">View SQL</Typography>
        {open ? <CollapseIcon sx={{ fontSize: 13 }} /> : <ExpandIcon sx={{ fontSize: 13 }} />}
      </Box>
      {open && (
        <Box component="pre" sx={{
          mt: 0.5, p: 1, borderRadius: 1, fontSize: 11, overflowX: 'auto',
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          color: 'success.main', fontFamily: 'monospace',
          whiteSpace: 'pre-wrap', wordBreak: 'break-all',
        }}>
          {sql}
        </Box>
      )}
    </Box>
  );
}

// ── Data Table ────────────────────────────────────────────────────────────────
function ResultTable({ data }) {
  const theme = useTheme();
  if (!data || data.length === 0) return null;

  const allCols     = Object.keys(data[0]);
  const visibleCols = allCols.filter(c => !HIDE_COLS.test(c));

  // ── Column value formatters ──────────────────────────────────────────────
  // Add keywords here to control how column values are formatted in the table
  const MONEY_PATTERN = /balance|amount|principal|payment|value|price|outstanding|origination|total|sum|proceeds|payoff|fee|charge/i;
  const PCT_PATTERN   = /rate|ltv|percent|ratio|yield|coupon|spread/i;
  const DATE_PATTERN  = /date|_at|_on$/i;

  const formatValue = (col, val) => {
    if (val == null || val === '') return '—';
    if (DATE_PATTERN.test(col)) {
      const d = new Date(val);
      if (!isNaN(d)) return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
    const num = parseFloat(val);
    if (!isNaN(num) && val !== true && val !== false) {
      if (MONEY_PATTERN.test(col) && num >= 100) return '$' + num.toLocaleString('en-US', { maximumFractionDigits: 0 });
      if (PCT_PATTERN.test(col) && num <= 100)   return num.toFixed(2) + '%';
      if (Number.isInteger(Number(val)) && num >= 1000) return num.toLocaleString('en-US');
    }
    return val;
  };

  const columns = visibleCols.map(col => ({
    field: col,
    headerName: col.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    flex: 1, minWidth: 100,
    renderCell: (params) => {
      const url = resolveEntityUrl(col, params.value);
      if (url) {
        if (INLINE_LINK_COLS.test(col)) {
          return (
            <Link href={url} target="_blank" rel="noreferrer"
              sx={{ color: 'primary.main', fontSize: 12, fontWeight: 600 }}>
              {params.value}
            </Link>
          );
        }
        return (
          <Link href={url} target="_blank" rel="noreferrer"
            sx={{ color: 'success.main', fontSize: 12,
                  display: 'flex', alignItems: 'center', gap: 0.3 }}>
            View <OpenInNewIcon sx={{ fontSize: 11 }} />
          </Link>
        );
      }
      return (
        <Typography variant="caption" sx={{ color: 'text.primary' }}>
          {formatValue(col, params.value)}
        </Typography>
      );
    },
  }));

  const rows = data.slice(0, 100).map((row, i) => ({ id: i, ...row }));

  return (
    <Box sx={{ mt: 1.5, width: '100%', maxHeight: 280 }}>
      <DataGrid
        rows={rows}
        columns={columns}
        density="compact"
        hideFooterPagination={rows.length <= 25}
        pageSizeOptions={[25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        disableColumnMenu
        sx={{
          fontSize: 12, border: 'none',
          '& .MuiDataGrid-columnHeaders': {
            bgcolor: alpha(theme.palette.primary.main, 0.12),
            color: 'text.secondary', fontSize: 11,
          },
          '& .MuiDataGrid-row:hover': {
            bgcolor: alpha(theme.palette.primary.main, 0.06),
          },
        }}
      />
      {data.length > 100 && (
        <Typography variant="caption" sx={{ color: 'text.disabled', mt: 0.5, display: 'block' }}>
          Showing first 100 of {data.length} rows
        </Typography>
      )}
    </Box>
  );
}

// ── Chart ─────────────────────────────────────────────────────────────────────
function ResultChart({ data, chartType, valueCol, labelCol }) {
  const theme = useTheme();
  if (!data || data.length === 0 || !chartType) return null;

  const allKeys = Object.keys(data[0]);

  // STRICT chart rules — PHP-provided cols are always trusted.
  // Without PHP cols, ONLY render when column name is an aggregate alias.
  // Aggregate aliases use snake_case with explicit prefix/suffix: loan_count, total_balance, avg_rate
  // Raw CamelCase columns (OriginalBalance, City, Description) are NEVER used.

  // Aggregate value pattern: must have explicit aggregate prefix OR suffix in snake_case
  const AGG_VALUE = /^(count_|total_|sum_|avg_|num_|min_|max_|.+_count$|.+_total$|.+_sum$|.+_avg$|.+_amount$|.+_balance$)/i;
  // Aggregate label pattern: must have explicit categorical suffix in snake_case
  const AGG_LABEL = /^(loan_status$|property_type$|loan_type$|status_name$|.+_status$|.+_type$|.+_month$|.+_year$|.+_quarter$|.+_category$)/i;

  // Use PHP-provided cols first (most accurate)
  const valueKey = valueCol ||
    allKeys.find(k => !HIDE_COLS.test(k) && AGG_VALUE.test(k) && !isNaN(parseFloat(data[0][k])));

  const labelKey = labelCol ||
    allKeys.find(k => !HIDE_COLS.test(k) && AGG_LABEL.test(k) && isNaN(parseFloat(data[0][k])));

  // Only render when PHP explicitly provided both cols, or both strict patterns matched
  // This prevents any guessing on raw query results
  if (!valueKey || !labelKey) return null;
  if (!valueCol && !labelCol && (!AGG_VALUE.test(valueKey) || !AGG_LABEL.test(labelKey))) return null;

  // Row count guard: aggregated results are small sets
  if (data.length > 20 || data.length < 2) return null;

  const palette = [
    theme.palette.primary.main, theme.palette.success.main,
    theme.palette.info.main,    theme.palette.warning.main,
    theme.palette.error.main,
  ];
  const formatted = data.map(row => ({
    label: String(row[labelKey] ?? ''),
    value: parseFloat(row[valueKey]) || 0,
  }));
  const commonProps = { height: 220, margin: { top: 10, bottom: 30, left: 40, right: 10 } };

  if (chartType === 'pie') {
    return (
      <PieChart
        series={[{ data: formatted.map((d, i) => ({
          id: i, value: d.value, label: d.label, color: palette[i % palette.length],
        })) }]}
        {...commonProps}
      />
    );
  }
  if (chartType === 'line') {
    return (
      <LineChart
        xAxis={[{ scaleType: 'band', data: formatted.map(d => d.label) }]}
        series={[{ data: formatted.map(d => d.value), color: theme.palette.primary.main, area: true }]}
        {...commonProps}
      />
    );
  }
  return (
    <BarChart
      xAxis={[{ scaleType: 'band', data: formatted.map(d => d.label), tickLabelStyle: { fontSize: 10 } }]}
      series={[{ data: formatted.map(d => d.value), color: theme.palette.primary.main }]}
      {...commonProps}
    />
  );
}

// ── Message Bubble ────────────────────────────────────────────────────────────
function MessageBubble({ msg }) {
  const theme = useTheme();

  if (msg.role === 'user') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
        <Box sx={{
          maxWidth: '78%', px: 1.5, py: 1,
          bgcolor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          borderRadius: '14px 14px 4px 14px',
          fontSize: 13, lineHeight: 1.6,
        }}>
          {msg.text}
        </Box>
      </Box>
    );
  }

  if (msg.role === 'nav') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1.5 }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          px: 1.5, py: 1, borderRadius: 2, maxWidth: '90%',
          border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
          bgcolor: alpha(theme.palette.success.main, 0.06),
        }}>
          <Typography sx={{ fontSize: 16 }}>🧭</Typography>
          <Box>
            <Typography variant="caption"
              sx={{ color: 'success.main', fontWeight: 600, display: 'block' }}>
              Navigating to {msg.label}
            </Typography>
            <Link href={msg.url} target={msg.newTab ? '_blank' : '_self'} rel="noreferrer"
              sx={{ fontSize: 11, color: 'text.disabled' }}>
              {msg.url}
            </Link>
          </Box>
        </Box>
      </Box>
    );
  }

  // assistant — Markdown rendered
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1.5 }}>
      <Box sx={{ maxWidth: '92%', width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
          <AIIcon sx={{ fontSize: 14, color: 'success.main' }} />
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>Archer</Typography>
          {msg.sql && (
            <Chip label="SQL" size="small" sx={{
              fontSize: 10, height: 16,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: 'text.secondary',
            }} />
          )}
        </Box>
        <Box sx={{
          px: 1.5, py: 1, borderRadius: '4px 14px 14px 14px',
          bgcolor: theme.palette.mode === 'dark'
            ? alpha(theme.palette.primary.main, 0.08)
            : alpha(theme.palette.primary.main, 0.05),
          border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          '& p:last-child': { mb: 0 },
          '& ul:last-child, & ol:last-child': { mb: 0 },
        }}>
          <MarkdownContent>{msg.text}</MarkdownContent>
          {msg.chartType && msg.data?.length > 0 && (
            <ResultChart
              data={msg.data}
              chartType={msg.chartType}
              valueCol={msg.chartValueCol || null}
              labelCol={msg.chartLabelCol || null}
            />
          )}
          {msg.data?.length > 0 && <ResultTable data={msg.data} />}
          <SqlDisclosure sql={msg.sql} />
        </Box>
      </Box>
    </Box>
  );
}

// ── Streaming Bubble (live text while response arrives) ───────────────────────
function StreamingBubble({ text, toolActive, theme }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1.5 }}>
      <Box sx={{ maxWidth: '92%', width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
          <AIIcon sx={{ fontSize: 14, color: 'success.main' }} />
          <Typography variant="caption" sx={{ color: 'text.disabled' }}>Archer</Typography>
          {toolActive && (
            <Chip
              icon={<DbIcon sx={{ fontSize: 10 }} />}
              label="Querying..."
              size="small"
              sx={{
                fontSize: 10, height: 16,
                bgcolor: alpha(theme.palette.warning.main, 0.1),
                color: 'warning.main',
              }}
            />
          )}
        </Box>
        <Box sx={{
          px: 1.5, py: 1, borderRadius: '4px 14px 14px 14px',
          bgcolor: theme.palette.mode === 'dark'
            ? alpha(theme.palette.primary.main, 0.08)
            : alpha(theme.palette.primary.main, 0.05),
          border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          '& p:last-child': { mb: 0 },
        }}>
          {text ? (
            <>
              <MarkdownContent>{text}</MarkdownContent>
              <Box component="span" sx={{
                display: 'inline-block', width: '2px', height: '1em',
                bgcolor: 'primary.main', ml: '2px', verticalAlign: 'text-bottom',
                animation: 'archerBlink 1s step-end infinite',
                '@keyframes archerBlink': { '50%': { opacity: 0 } },
              }} />
            </>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={12} sx={{ color: 'success.main' }} />
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                {toolActive ? 'Running query...' : 'Thinking...'}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

// ── Main Widget ───────────────────────────────────────────────────────────────
export default function ArcherChat({ pageContext }) {
  const dispatch  = useDispatch();
  const theme     = useTheme();
  const bottomRef = useRef(null);
  const inputRef  = useRef(null);

  const {
    open, loading, messages, error,
    conversationId, streamingText, toolActive,
  } = useSelector(state => state.ArcherChat);

  const [input, setInput] = React.useState('');
  const [width, setWidth] = React.useState(420);
  const isResizing = React.useRef(false);
  const startX = React.useRef(0);
  const startWidth = React.useRef(420);

  const onMouseDown = useCallback((e) => {
    isResizing.current = true;
    startX.current = e.clientX;
    startWidth.current = width;
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
    const onMouseMove = (e) => {
      if (!isResizing.current) return;
      const delta = startX.current - e.clientX;
      const newWidth = Math.min(800, Math.max(320, startWidth.current + delta));
      setWidth(newWidth);
    };
    const onMouseUp = () => {
      isResizing.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [width]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading, streamingText]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);

  useEffect(() => {
    return () => { dispatch(archerNewConversation()); };
  }, []); // empty deps — runs cleanup only on unmount

  const handleSend = useCallback(() => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    dispatch(archerSendMessage(text, conversationId, pageContext));
  }, [input, loading, conversationId, pageContext, dispatch]);

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const SUGGESTIONS = [
    'How many active loans?',
    'Show foreclosure properties',
    'Go to loans dashboard',
    'Top 5 loans by amount',
  ];

  return (
    <>
      {/* Floating trigger button */}
      <Tooltip title="Archer AI Assistant" placement="left">
        <Fab
          onClick={() => dispatch(archerToggleWidget())}
          sx={{
            position: 'fixed', bottom: 24, right: 24, zIndex: 1300,
            bgcolor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            boxShadow: 4,
            '&:hover': { bgcolor: theme.palette.primary.dark },
            visibility: open ? 'hidden' : 'visible',
          }}
        >
          {open ? <CloseIcon /> : <AIIcon />}
        </Fab>
      </Tooltip>

      {/* Chat panel */}
      <Drawer
        anchor="right"
        open={open}
        variant="persistent"
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100vw', sm: width },
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
            borderLeft: `1px solid ${theme.palette.divider}`,
            boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
            zIndex: 1299,
            overflow: 'visible',
          },
        }}
      >
        {/* Resize handle */}
        <Box
          onMouseDown={onMouseDown}
          sx={{
            position: 'absolute', left: -4, top: 0, bottom: 0, width: 8,
            cursor: 'ew-resize', zIndex: 10,
            display: { xs: 'none', sm: 'flex' },
            alignItems: 'center', justifyContent: 'center',
            '&:hover > div, &:active > div': { bgcolor: 'primary.main', opacity: 1 },
          }}
        >
          <Box sx={{
            width: 3, height: 40, borderRadius: 2,
            bgcolor: 'divider', opacity: 0.5, transition: 'all 0.15s',
          }} />
        </Box>
        {/* Header */}
        <Box sx={{
          px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.mode === 'dark'
            ? alpha(theme.palette.primary.main, 0.15)
            : alpha(theme.palette.primary.main, 0.06),
        }}>
          <AIIcon sx={{ color: 'success.main', fontSize: 22 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: 14 }}>
              ARCHER
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled', letterSpacing: 1 }}>
              AI DATABASE ASSISTANT
            </Typography>
          </Box>
          <Tooltip title="New conversation">
            <IconButton size="small" onClick={() => dispatch(archerNewConversation())}
              sx={{ color: 'text.secondary' }}>
              <NewChatIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={() => dispatch(archerToggleWidget())}
            sx={{ color: 'text.secondary' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Messages area */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2 }}>

          {/* Empty state */}
          {messages.length === 0 && !loading && (
            <Box sx={{ textAlign: 'center', mt: 4, mb: 2 }}>
              <AIIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h6" sx={{ mb: 0.5, fontSize: 15 }}>
                Ask about your data
              </Typography>
              <Typography variant="caption"
                sx={{ color: 'text.disabled', display: 'block', mb: 2.5 }}>
                Queries · Reports · Navigation
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                {SUGGESTIONS.map(s => (
                  <Chip key={s} label={s} size="small" onClick={() => setInput(s)}
                    sx={{
                      cursor: 'pointer', fontSize: 11,
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.16) },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Message list */}
          {messages.map((msg, i) => (
            <MessageBubble key={i} msg={msg} />
          ))}

          {/* Live streaming bubble */}
          {loading && (
            <StreamingBubble
              text={streamingText}
              toolActive={toolActive}
              theme={theme}
            />
          )}

          {/* Error */}
          {error && (
            <Box sx={{
              p: 1.5, borderRadius: 1, mb: 1,
              bgcolor: alpha(theme.palette.error.main, 0.08),
              border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
            }}>
              <Typography variant="caption" sx={{ color: 'error.main' }}>⚠ {error}</Typography>
            </Box>
          )}

          <div ref={bottomRef} />
        </Box>

        <Divider />

        {/* Input area */}
        <Box sx={{ px: 2, py: 1.5, display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <TextField
            inputRef={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Ask about your data..."
            multiline
            maxRows={4}
            size="small"
            fullWidth
            disabled={loading}
            sx={{ '& .MuiOutlinedInput-root': { fontSize: 13 } }}
          />
          <IconButton
            onClick={handleSend}
            disabled={loading || !input.trim()}
            sx={{
              bgcolor: input.trim() && !loading ? 'primary.main' : 'action.disabled',
              color: input.trim() && !loading ? 'primary.contrastText' : 'text.disabled',
              borderRadius: 1.5, p: 1,
              '&:hover': { bgcolor: 'primary.dark' },
              transition: 'all 0.2s',
            }}
          >
            <SendIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: 0.5, pb: 1, pt: 0.5 }}>

          <Typography variant="caption"
            sx={{ color: 'text.disabled', fontSize: 10 }}>
            Powered by
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 10, fontStyle: 'italic', letterSpacing: 0.3 }}>
            Claude
          </Typography>
          <Typography variant="caption"
            sx={{ color: 'text.disabled', fontSize: 10 }}>
            · v1.1
          </Typography>
        </Box>
      </Drawer>
    </>
  );
}